import React from 'react';
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';

const buttonVariants = cva(
    "inline-flex items-center justify-center transition-all duration-200 font-medium focus:ring-2 focus:outline-none focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                // Primary variants (original naming)
                primary: "bg-primary text-primary-foreground hover:bg-primary/90",
                default: "bg-primary text-primary-foreground hover:bg-primary/90", // alias for primary
                
                // Secondary variants
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                
                // Status variants
                success: "bg-success text-white hover:bg-opacity-90 active:bg-opacity-100",
                danger: "bg-error text-white hover:bg-opacity-90 active:bg-opacity-100",
                destructive: "bg-error text-white hover:bg-opacity-90 active:bg-opacity-100", // alias for danger
                warning: "bg-warning text-primary-foreground hover:bg-opacity-90 active:bg-opacity-100",
                info: "bg-accent text-white hover:bg-opacity-90 active:bg-opacity-100",
                
                // Interactive variants
                ghost: "hover:bg-primary hover:text-primary-foreground",
                link: "bg-transparent text-primary-foreground underline hover:text-primary-600 p-0",
                outline: "border border-input bg-background hover:bg-primary hover:text-primary-foreground",
                text: "bg-transparent text-primary-foreground hover:bg-surface hover:bg-opacity-50 active:bg-surface active:bg-opacity-70",
            },
            size: {
                // Original size system
                '2xs': 'text-xs py-0.5 px-1.5',
                xs: 'text-xs py-1 px-2',
                sm: 'text-sm py-1.5 px-3',
                md: 'text-base py-2 px-4',
                default: 'text-base py-2 px-4', // alias for md
                lg: 'text-lg py-2.5 px-5',
                xl: 'text-xl py-3 px-6',
                '2xl': 'text-2xl py-4 px-8',
                
                // Icon-only button
                icon: "h-10 w-10",
            },
            shape: {
                rounded: 'rounded',
                square: 'rounded-none',
                pill: 'rounded-full',
                circle: 'rounded-full aspect-square',
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "md",
            shape: "rounded",
        },
    }
);

const Button = React.forwardRef(({
    className,
    variant = 'primary',
    size = 'md',
    shape = 'rounded',
    asChild = false,
    children,
    fullWidth = false,
    disabled = false,
    loading = false,
    
    // Icon props (supporting both patterns)
    icon = null,
    iconName = null,
    iconPosition = 'left',
    iconSize = null,
    iconColor = null,
    
    type = 'button',
    onClick,
    ...props
}, ref) => {
    const Comp = asChild ? Slot : "button";
    const mirroredIconNames = new Set([
        'ArrowLeft',
        'ArrowRight',
        'ChevronLeft',
        'ChevronRight',
        'MoveLeft',
        'MoveRight',
        'SkipBack',
        'SkipForward',
    ]);

    // Icon size mapping based on button size
    const iconSizeMap = {
        '2xs': 12,
        xs: 14,
        sm: 16,
        md: 18,
        default: 18,
        lg: 20,
        xl: 22,
        '2xl': 24,
        icon: 16,
    };

    const calculatedIconSize = iconSize || iconSizeMap[size] || 18;

    // Loading spinner
    const LoadingSpinner = () => (
        <svg className="animate-spin -ms-1 me-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    // Icon rendering with support for both iconName and icon props
    const renderIcon = () => {
        // Handle iconName (AppIcon component)
        if (iconName) {
            try {
                const spacingClass = children ? (iconPosition === 'left' ? 'me-2' : 'ms-2') : '';
                const mirrorClass = mirroredIconNames.has(iconName) ? 'rtl:scale-x-[-1]' : '';
                
                return (
                    <span 
                        style={{ color: iconColor || 'currentColor' }}
                        className={cn(spacingClass, mirrorClass)}
                    >
                        <Icon
                            name={iconName}
                            size={calculatedIconSize}
                        />
                    </span>
                );
            } catch (error) {
                console.warn('Icon component failed to render:', error);
                return null;
            }
        }

        // Handle icon prop (React element)
        if (icon && React.isValidElement(icon)) {
            const spacingClass = children ? (iconPosition === 'left' ? 'me-2' : 'ms-2') : '';
            
            return React.cloneElement(icon, {
                className: cn(spacingClass, 'h-5 w-5', icon.props.className)
            });
        }

        return null;
    };

    // Content to render inside button
    const buttonContent = (
        <>
            {loading && <LoadingSpinner />}
            {(icon || iconName) && iconPosition === 'left' && renderIcon()}
            {children}
            {(icon || iconName) && iconPosition === 'right' && renderIcon()}
        </>
    );

    // Generate button classes
    const buttonClasses = cn(
        buttonVariants({ variant, size, shape }),
        fullWidth && "w-full",
        className
    );

    // Fallback button for error cases
    const renderFallbackButton = () => (
        <button
            ref={ref}
            type={type}
            className={buttonClasses}
            disabled={disabled || loading}
            onClick={onClick}
            {...props}
        >
            {buttonContent}
        </button>
    );

    // Handle asChild prop (Radix Slot pattern)
    if (asChild) {
        try {
            if (!children || React.Children.count(children) !== 1) {
                return renderFallbackButton();
            }

            const child = React.Children.only(children);

            if (!React.isValidElement(child)) {
                return renderFallbackButton();
            }

            // Clone the child with button properties
            const clonedChild = React.cloneElement(child, {
                className: cn(buttonClasses, child.props.className),
                disabled: disabled || loading || child.props.disabled,
                children: (
                    <>
                        {loading && <LoadingSpinner />}
                        {(icon || iconName) && iconPosition === 'left' && renderIcon()}
                        {child.props.children}
                        {(icon || iconName) && iconPosition === 'right' && renderIcon()}
                    </>
                ),
            });

            return <Comp ref={ref} {...props}>{clonedChild}</Comp>;
        } catch (error) {
            console.warn('AsChild rendering failed:', error);
            return renderFallbackButton();
        }
    }

    // Regular button rendering
    return (
        <Comp
            ref={ref}
            type={type}
            className={buttonClasses}
            disabled={disabled || loading}
            onClick={onClick}
            {...props}
        >
            {buttonContent}
        </Comp>
    );
});

Button.displayName = "Button";

export default Button;
